/**
 * Admin Reviews List Page — /reviews
 *
 * Tab-based review queue with bulk actions and side panel detail view.
 * Follows existing dashboard patterns (PageContainer, Card, React Query).
 */

import { useState, useCallback, useRef, useMemo } from "react"
import { PageContainer } from "@/components/layout/PageContainer"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Search, Image as ImageIcon, Check, X } from "lucide-react"
import { StarRating } from "./components/StarRating"
import { ReviewStatusBadge } from "./components/ReviewStatusBadge"
import { ReviewDetailPanel } from "./components/ReviewDetailPanel"
import {
  useReviewList,
  useBulkModerateReviews,
} from "./api/reviewHooks"
import type { ReviewItem, ReviewStatus } from "./api/reviewTypes"

function formatRelativeDate(dateStr: string): string {
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return "just now"
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

const TAB_OPTIONS: { value: ReviewStatus; label: string }[] = [
  { value: "Pending", label: "Pending" },
  { value: "Approved", label: "Approved" },
  { value: "Rejected", label: "Rejected" },
  { value: "All", label: "All" },
]

export function ReviewsListPage() {
  const [activeTab, setActiveTab] = useState<ReviewStatus>("Pending")
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [selectedName, setSelectedName] = useState<string | null>(null)
  const selectedSnapshotRef = useRef<ReviewItem | null>(null)
  const [panelOpen, setPanelOpen] = useState(false)
  const [selectedNames, setSelectedNames] = useState<Set<string>>(new Set())

  const { data, isLoading } = useReviewList(activeTab, page, 20, debouncedSearch)
  const bulkModerateMutation = useBulkModerateReviews()

  const reviews = data?.reviews ?? []
  const total = data?.total ?? 0
  const pendingCount = data?.pending_count ?? 0

  // Derive the open panel's review from the LIVE list so it reflects refetched
  // data (seller reply posted/edited/deleted, status change) instead of a frozen
  // snapshot. Fall back to the last snapshot when the row leaves the current
  // tab filter (e.g. after approve in the Pending tab — the panel is closing).
  const selectedReview = useMemo(() => {
    const live = reviews.find((r) => r.name === selectedName)
    if (live) {
      selectedSnapshotRef.current = live
      return live
    }
    return selectedName ? selectedSnapshotRef.current : null
  }, [reviews, selectedName])

  // Debounced search
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value)
      clearTimeout(searchTimerRef.current)
      searchTimerRef.current = setTimeout(() => {
        setDebouncedSearch(value)
        setPage(1)
      }, 300)
    },
    [],
  )

  const handleTabChange = (value: string) => {
    setActiveTab(value as ReviewStatus)
    setPage(1)
    setSelectedNames(new Set())
  }

  const handleRowClick = (review: ReviewItem) => {
    selectedSnapshotRef.current = review
    setSelectedName(review.name)
    setPanelOpen(true)
  }

  const toggleSelection = (name: string) => {
    setSelectedNames((prev) => {
      const next = new Set(prev)
      if (next.has(name)) {
        next.delete(name)
      } else {
        next.add(name)
      }
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedNames.size === reviews.length) {
      setSelectedNames(new Set())
    } else {
      setSelectedNames(new Set(reviews.map((r) => r.name)))
    }
  }

  const handleBulkAction = (action: "approve" | "reject") => {
    const names = Array.from(selectedNames)
    if (names.length === 0) return
    bulkModerateMutation.mutate(
      { reviewNames: names, action },
      { onSuccess: () => setSelectedNames(new Set()) },
    )
  }

  const totalPages = Math.ceil(total / 20)

  return (
    <PageContainer>
      <div className="p-6 space-y-5">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-semibold">Reviews</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Manage and moderate customer reviews
          </p>
        </div>

        {/* Pending count stats bar */}
        {pendingCount > 0 && (
          <div className="flex items-center gap-2 text-sm">
            <span className="h-2 w-2 rounded-full bg-amber-500" />
            <span className="font-medium">
              {pendingCount} pending review{pendingCount !== 1 ? "s" : ""}
            </span>
          </div>
        )}

        {/* Tabs + search */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <Tabs
            value={activeTab}
            onValueChange={handleTabChange}
          >
            <TabsList>
              {TAB_OPTIONS.map((tab) => {
                const count =
                  tab.value === "Pending"
                    ? data?.pending_count
                    : tab.value === "Approved"
                      ? data?.approved_count
                      : tab.value === "Rejected"
                        ? data?.rejected_count
                        : total
                return (
                  <TabsTrigger key={tab.value} value={tab.value}>
                    {tab.label}
                    {count !== undefined && count > 0 && (
                      <span className="ml-1.5 text-xs text-muted-foreground">
                        ({count})
                      </span>
                    )}
                  </TabsTrigger>
                )
              })}
            </TabsList>
          </Tabs>

          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search product or reviewer..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-9 h-9"
            />
          </div>
        </div>

        {/* Review table */}
        <Card className="rounded-2xl border-0">
          <CardContent className="p-0">
            {isLoading ? (
              <ReviewListSkeleton />
            ) : reviews.length === 0 ? (
              <ReviewEmptyState activeTab={activeTab} />
            ) : (
              <div className="divide-y divide-border/30">
                {/* Select all header */}
                <div className="flex items-center gap-3 px-5 py-3 bg-muted/30">
                  <Checkbox
                    checked={
                      reviews.length > 0 &&
                      selectedNames.size === reviews.length
                    }
                    onCheckedChange={toggleSelectAll}
                    aria-label="Select all reviews"
                  />
                  <span className="text-xs text-muted-foreground">
                    {selectedNames.size > 0
                      ? `${selectedNames.size} selected`
                      : "Select all"}
                  </span>
                </div>

                {reviews.map((review) => (
                  <ReviewRow
                    key={review.name}
                    review={review}
                    selected={selectedNames.has(review.name)}
                    onToggle={() => toggleSelection(review.name)}
                    onClick={() => handleRowClick(review)}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setPage((p) => Math.max(1, p - 1)); setSelectedNames(new Set()) }}
              disabled={page <= 1}
            >
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setPage((p) => Math.min(totalPages, p + 1)); setSelectedNames(new Set()) }}
              disabled={page >= totalPages}
            >
              Next
            </Button>
          </div>
        )}

        {/* Bulk actions floating bar */}
        {selectedNames.size > 0 && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 bg-background border rounded-2xl shadow-lg px-5 py-3">
            <Button
              size="sm"
              onClick={() => handleBulkAction("approve")}
              disabled={bulkModerateMutation.isPending}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Check className="mr-1.5 h-3.5 w-3.5" />
              Approve Selected ({selectedNames.size})
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleBulkAction("reject")}
              disabled={bulkModerateMutation.isPending}
              className="border-destructive/30 text-destructive hover:bg-destructive/10"
            >
              <X className="mr-1.5 h-3.5 w-3.5" />
              Reject Selected
            </Button>
          </div>
        )}
      </div>

      {/* Detail panel */}
      <ReviewDetailPanel
        review={selectedReview}
        open={panelOpen}
        onOpenChange={setPanelOpen}
      />
    </PageContainer>
  )
}

/* ───────────────────────── Sub-components ───────────────────────── */

function ReviewRow({
  review,
  selected,
  onToggle,
  onClick,
}: {
  review: ReviewItem
  selected: boolean
  onToggle: () => void
  onClick: () => void
}) {
  const displayName = review.is_anonymous
    ? "Anonymous Buyer"
    : review.customer_name
  const snippet =
    review.review_text && review.review_text.length > 60
      ? review.review_text.slice(0, 60) + "..."
      : review.review_text || "(No text)"
  const mediaCount = review.media?.length ?? 0

  return (
    <div
      className="flex items-center gap-3 px-5 py-4 hover:bg-muted/40 cursor-pointer transition-colors"
      onClick={onClick}
    >
      <div onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={selected}
          onCheckedChange={onToggle}
          aria-label={`Select review by ${displayName}`}
        />
      </div>

      {/* Product thumbnail */}
      {review.item_image ? (
        <img
          src={review.item_image}
          alt={review.item_name}
          className="w-10 h-10 rounded-lg object-cover bg-muted shrink-0"
        />
      ) : (
        <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
          <ImageIcon className="w-4 h-4 text-muted-foreground" />
        </div>
      )}

      {/* Review content */}
      <div className="flex-1 min-w-0 space-y-0.5">
        <p className="text-sm font-medium truncate">{review.item_name}</p>
        <div className="flex items-center gap-2">
          <StarRating rating={review.rating} size={12} />
          <span className="text-xs text-muted-foreground truncate">
            {displayName}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <p className="text-xs text-muted-foreground truncate">{snippet}</p>
          {mediaCount > 0 && (
            <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground shrink-0">
              <ImageIcon className="w-3 h-3" />
              {mediaCount}
            </span>
          )}
        </div>
      </div>

      {/* Date + status */}
      <div className="flex flex-col items-end gap-1 shrink-0">
        <span className="text-xs text-muted-foreground">
          {formatRelativeDate(review.creation)}
        </span>
        <ReviewStatusBadge status={review.status} />
      </div>
    </div>
  )
}

function ReviewListSkeleton() {
  return (
    <div className="divide-y divide-border/30">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-5 py-4">
          <Skeleton className="w-4 h-4 rounded" />
          <Skeleton className="w-10 h-10 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-3 w-56" />
          </div>
          <div className="space-y-1">
            <Skeleton className="h-3 w-16 ml-auto" />
            <Skeleton className="h-5 w-16" />
          </div>
        </div>
      ))}
    </div>
  )
}

function ReviewEmptyState({
  activeTab,
}: {
  activeTab: ReviewStatus
}) {
  const messages: Record<ReviewStatus, { title: string; subtitle: string }> = {
    Pending: {
      title: "No pending reviews",
      subtitle:
        "When customers submit reviews, they will appear here for your approval.",
    },
    Approved: {
      title: "No approved reviews",
      subtitle: "Approved reviews will appear here.",
    },
    Rejected: {
      title: "No rejected reviews",
      subtitle: "Rejected reviews will appear here.",
    },
    All: {
      title: "No reviews yet",
      subtitle:
        "Once customers start reviewing products, they will appear here for your approval.",
    },
  }

  const { title, subtitle } = messages[activeTab]

  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
      <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
        <StarRating rating={0} size={20} />
      </div>
      <h3 className="text-base font-medium mb-1">{title}</h3>
      <p className="text-sm text-muted-foreground max-w-sm">{subtitle}</p>
    </div>
  )
}
